import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Bot, Check, Newspaper, Palette, Users, X } from "lucide-react";
import api from "../lib/api";
import { useSession } from "../lib/session";

/* The four things that turn an empty workspace into one that runs itself,
   checked against what actually exists rather than a flag someone ticked.
   Disappears once they are all done, or when dismissed. */
const STEPS = [
  { key: "brand", view: "brands", icon: Palette, title: "Set up your brand",
    body: "Palette, type and voice - or upload your guidelines PDF and let it fill itself in." },
  { key: "account", view: "accounts", icon: Users, title: "Connect a channel",
    body: "LinkedIn, an Instagram account or a Facebook page to publish to." },
  { key: "feed", view: "news", icon: Newspaper, title: "Add a news feed",
    body: "Sources it reads for fresh stories - any site with an RSS feed." },
  { key: "autopilot", view: "autopilot", icon: Bot, title: "Turn on autopilot",
    body: "Set the weekly target and how much to trust it. It takes it from there." },
];

const dismissKey = (ws) => `autocruise_onboarding_dismissed_${ws}`;

export const Onboarding = ({ goTo }) => {
  const { me } = useSession();
  const [done, setDone] = useState(null);
  const [hidden, setHidden] = useState(false);
  const ws = me?.workspace?.id;

  useEffect(() => {
    if (!ws) return;
    try {
      if (localStorage.getItem(dismissKey(ws))) { setHidden(true); return; }
    } catch (e) { /* storage blocked: just show it */ }
    Promise.all([api.get("/brands"), api.get("/accounts"), api.get("/sources"), api.get("/settings")])
      .then(([b, a, s, st]) => setDone({
        brand: b.data.length > 0, account: a.data.length > 0,
        feed: s.data.length > 0, autopilot: Boolean(st.data.autopilot_enabled),
      }))
      .catch(() => setHidden(true));
  }, [ws]);

  if (hidden || !done) return null;
  const count = STEPS.filter((s) => done[s.key]).length;
  if (count === STEPS.length) return null;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey(ws), "1"); } catch (e) { /* fine */ }
    setHidden(true);
  };
  const pct = (count / STEPS.length) * 100;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        className="relative mb-10 overflow-hidden rounded-3xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/[0.08] via-slate-900/40 to-fuchsia-500/[0.06] p-6 sm:p-8"
        data-testid="onboarding">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-fuchsia-500/10 blur-3xl" aria-hidden="true" />
        <button onClick={dismiss} aria-label="Dismiss" className="absolute top-4 right-4 p-1.5 text-slate-500 hover:text-slate-200">
          <X className="w-4 h-4" />
        </button>
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="relative w-16 h-16 shrink-0">
            <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
              <motion.circle cx="18" cy="18" r="15.5" fill="none" stroke="url(#ob-grad)" strokeWidth="3" strokeLinecap="round"
                strokeDasharray="97.4" initial={{ strokeDashoffset: 97.4 }} animate={{ strokeDashoffset: 97.4 * (1 - pct / 100) }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} />
              <defs><linearGradient id="ob-grad"><stop offset="0" stopColor="#818cf8" /><stop offset="1" stopColor="#f472b6" /></linearGradient></defs>
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">{count}/{STEPS.length}</span>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-white">Get {me?.workspace?.name} running on its own</h2>
            <p className="text-sm text-slate-400 mt-1">Four steps, a few minutes each. After that, it fills the calendar by itself.</p>
          </div>
        </div>
        <div className="relative mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(({ key, view, icon: Icon, title, body }, i) => {
            const ok = done[key];
            const next = !ok && STEPS.slice(0, i).every((s) => done[s.key]);
            return (
              <motion.button key={key} onClick={() => goTo(view)} whileHover={{ y: -3 }}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.07 }}
                className={`text-left rounded-2xl border p-4 transition-colors ${ok ? "border-emerald-500/20 bg-emerald-500/[0.05]"
                  : next ? "border-indigo-400/40 bg-indigo-500/[0.08]" : "border-slate-800 bg-slate-900/30 hover:border-slate-700"}`}>
                <div className="flex items-center justify-between">
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${ok ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800/80 text-slate-300"}`}>
                    {ok ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </span>
                  {next && <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-300">Next</span>}
                </div>
                <p className={`mt-3 text-sm font-semibold ${ok ? "text-slate-400 line-through decoration-slate-600" : "text-slate-100"}`}>{title}</p>
                <p className="mt-1 text-xs text-slate-500 leading-relaxed">{body}</p>
                {!ok && <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-indigo-300">Start <ArrowRight className="w-3 h-3" /></span>}
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
